import { MouseEventHandler, useEffect, useState, useContext, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
  Popover,
  Grid,
  Skeleton,
  Typography,
  useTheme,
  alpha,
} from '@mui/material'
import moment from 'moment-timezone'

import { MultipleSwitch } from '../multiple-switch'
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded'
import BusinessIcon from '@mui/icons-material/Business'
import { HomeIcon, UserIcon } from 'src/assets/icons'
import { useAccount, useLogout, useMe } from 'src/hooks'

import { ROUTES, FONTS } from 'src/constants'
import { useStyles } from './styles'
import { ETranslationNs, EUserRoles, IAccountByIdRespDto, IProfileUserResponse } from 'src/types'
import { joinAbsRoute } from 'src/common/utils'
import { UserAddress } from './user-address.component'
import { ThemeSwitchContext } from 'src/providers/theme-provider'
import { GridIcon } from 'src/assets/icons/v2/GridIcon'
import { UserWalletsBallatceList } from '../user-wallets-ballance-list'

interface IUserPopoverProps {
  open: boolean
  anchorRef: React.RefObject<HTMLElement>
  handleClose: MouseEventHandler<HTMLButtonElement>
}

const UserPopover = ({ anchorRef, open, handleClose }: IUserPopoverProps) => {
  const [t] = useTranslation(ETranslationNs.Navbar)
  const { setWhiteLable } = useContext(ThemeSwitchContext)
  const { data: account } = useAccount()
  const { data: user, refetch } = useMe()
  const { logout } = useLogout()
  const navigate = useNavigate()
  const [time, setTime] = useState(new Date())
  const userTimezoneRef = useRef<HTMLElement>(null)
  const userEmailRef = useRef<HTMLElement>(null)
  const [userTimezoneWidth, setUserTimezoneWidth] = useState<number | null>(null)

  const classes = useStyles()
  const theme = useTheme()

  useEffect(() => {
    if (userTimezoneRef.current || userEmailRef.current) {
      setUserTimezoneWidth(
        Math.max(
          userTimezoneRef?.current?.offsetWidth || 0,
          userEmailRef?.current?.offsetWidth || 0,
        ),
      )
    }
  }, [userTimezoneRef.current, userEmailRef.current])

  useEffect(() => {
    const interval = setInterval(() => {
      setTime(new Date())
    }, 5000)

    return () => {
      clearInterval(interval)
    }
  }, [])

  const handleLogout = async () => {
    setWhiteLable(undefined)
    await refetch()

    await logout()
    navigate(ROUTES.LOGIN)
  }

  const handleNavigate = (path: string) => () => navigate(path)

  const getRolesString = (
    user: IProfileUserResponse | null | undefined | void,
    account: IAccountByIdRespDto | null | undefined | void,
  ) => {
    const superUsserRole = user?.roles?.includes(EUserRoles.superadmin)
      ? t('accounts-superadmin-role.v', { ns: ETranslationNs.Navbar })
      : ''
    const accountRole =
      account && account.currentAccountRoleForUser
        ? account.currentAccountRoleForUser.charAt(0).toUpperCase() +
          account.currentAccountRoleForUser.slice(1)
        : ''

    const divider = accountRole && superUsserRole ? ' | ' : ''

    return superUsserRole + divider + accountRole
  }

  const mz = moment(time).tz(user?.timezone ?? 'UTC')

  return (
    <Popover
      open={open}
      anchorEl={anchorRef.current}
      onClose={handleClose}
      anchorOrigin={{
        vertical: 'bottom',
        horizontal: 'center',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'center',
      }}
    >
      <Paper>
        <List>
          {account || user ? (
            <ListItem disablePadding className={classes.listItem2}>
              <ListItemText
                secondary={getRolesString(user, account)}
                secondaryTypographyProps={{
                  className: `${classes.popOverRole} `,
                }}
              />
            </ListItem>
          ) : null}
          {account?.account_holder && (
            <ListItem disablePadding className={classes.listItem2}>
              <ListItemText
                primary={account.account_holder}
                primaryTypographyProps={{
                  className: `${classes.popOverPrimary} ${classes.primaryAccent} ${classes.wrapText}`,
                }}
              />
            </ListItem>
          )}
          <ListItem style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
            <ListItemText
              ref={userEmailRef}
              secondary={user?.name}
              secondaryTypographyProps={{ className: classes.popOverDescr }}
              primaryTypographyProps={{ className: classes.popOverPrimary }}
              primary={user?.email}
            />
            <UserAddress
              address={user?.wallets?.find((wallet) => wallet.active)?.address || ''}
              maxWidth={userTimezoneWidth}
            />
          </ListItem>

          <ListItem sx={{ p: '4px 16px 12px' }}>
            <UserWalletsBallatceList />
          </ListItem>

          {user?.accounts && user.accounts.length === 0 && (
            <ListItem divider disablePadding>
              <ListItemButton
                onClick={handleNavigate(`/${ROUTES.ACCOUNTS}`)}
                sx={{
                  backgroundColor: theme.palette.custom.lightGreen,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.custom.lightGreen, 0.7),
                  },
                }}
              >
                <ListItemIcon className={classes.popOverIconWrapper}>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ className: classes.popOverPrimary }}
                  primary={t('account-create-business-account-title.v')}
                />
              </ListItemButton>
            </ListItem>
          )}

          {user?.accounts && user.accounts.length > 0 && (
            <ListItem divider disablePadding>
              <ListItemButton
                onClick={handleNavigate(
                  account?.isSingleAccount
                    ? joinAbsRoute(ROUTES.ACCOUNTS, account.id, ROUTES.SCAN_HISTORY)
                    : `/${ROUTES.ACCOUNTS}`,
                )}
                sx={{
                  backgroundColor: theme.palette.custom.lightGreen,
                  '&:hover': {
                    backgroundColor: alpha(theme.palette.custom.lightGreen, 0.7),
                  },
                }}
              >
                <ListItemIcon className={classes.popOverIconWrapper}>
                  <HomeIcon />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{ className: classes.popOverPrimary }}
                  primary={
                    account?.isSingleAccount
                      ? t('scan-history-header-title.v')
                      : t('accounts-header-title.v')
                  }
                />
              </ListItemButton>
            </ListItem>
          )}

          <ListItem divider disablePadding>
            <ListItemButton
              onClick={handleNavigate(joinAbsRoute(ROUTES.USER, ROUTES.USER_DASHBOARD))}
            >
              <ListItemIcon className={classes.popOverIconWrapper}>
                <GridIcon />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ className: classes.popOverPrimary }}
                primary={t('user-dashboard-header-title.v')}
              />
            </ListItemButton>
          </ListItem>

          <ListItem divider disablePadding>
            <ListItemButton
              onClick={handleNavigate(
                account?.isSingleAccount
                  ? joinAbsRoute(ROUTES.ACCOUNTS, account.id, ROUTES.PROFILE)
                  : `/${ROUTES.PROFILE}`,
              )}
            >
              <ListItemIcon className={classes.popOverIconWrapper}>
                <UserIcon />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ className: classes.popOverPrimary }}
                primary={t('user-profile-header-title.v')}
              />
            </ListItemButton>
          </ListItem>
          <ListItem disablePadding divider={user?.roles?.includes(EUserRoles.superadmin)}>
            <ListItemButton onClick={handleLogout}>
              <ListItemIcon className={classes.popOverIconWrapper}>
                <LogoutRoundedIcon className={classes.popOverIcon} />
              </ListItemIcon>
              <ListItemText
                primaryTypographyProps={{ className: classes.popOverPrimary }}
                primary={t('log-out-button-text.v')}
              />
            </ListItemButton>
          </ListItem>
          {user?.roles?.includes(EUserRoles.superadmin) && (
            <ListItem disablePadding>
              <ListItemButton onClick={handleNavigate(joinAbsRoute(ROUTES.ADMIN_ROUTES))}>
                <ListItemIcon className={classes.popOverIconWrapper}>
                  <BusinessIcon className={`${classes.popOverIcon} ${classes.popOverLight}`} />
                </ListItemIcon>
                <ListItemText
                  primaryTypographyProps={{
                    className: `${classes.popOverPrimary} ${classes.popOverLight}`,
                  }}
                  primary={t('admin-button-text.v')}
                />
              </ListItemButton>
            </ListItem>
          )}
          <ListItem sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <MultipleSwitch size="small" />
          </ListItem>
          <ListItem>
            <Grid container direction={'column'} alignItems={'start'}>
              <Grid item>
                <Typography color={'primary'} fontSize={FONTS.SIZES.m} sx={{ direction: 'row' }}>
                  {user ? (
                    <>{mz.format('HH:mm dddd,')} &nbsp;</>
                  ) : (
                    <Skeleton variant="text" width={100} height={21} sx={{ mr: 0.5 }} />
                  )}
                </Typography>
              </Grid>
              <Grid item>
                <Typography
                  ref={userTimezoneRef}
                  color={'inherit'}
                  fontSize={FONTS.SIZES.m}
                  sx={{ direction: 'row' }}
                >
                  {user ? (
                    <>
                      {mz.format('D MMMM YYYY')} (
                      {user ? (user?.timezone.replace('_', ' ') ?? 'UTC') : 'UTC'})
                    </>
                  ) : (
                    <Skeleton variant="text" width={200} height={21} />
                  )}
                </Typography>
              </Grid>
            </Grid>
          </ListItem>
        </List>
      </Paper>
    </Popover>
  )
}

export { UserPopover }
